import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Tooltip } from 'antd';
import type { TFunction } from 'i18next';
import type { MeasurementType, MydayType } from '../fetchHooks/useMyday';
import { formatDateOnly, getHealthMetricDescription } from './utilFunctions';

interface MydayActionHandlers {
  onView: (item: MydayType) => void;
  onEdit: (item: MydayType) => void;
  onDelete: (id: number) => void;
}

/**
 * Common column definitions for Myday tables
 */

export const createDateColumn = (t: TFunction, i18nLanguage: string, onView: (item: MydayType) => void) => ({
  title: t('common.date'),
  dataIndex: 'date',
  defaultSortOrder: 'descend' as const,
  sorter: (a: MydayType, b: MydayType) => a.date.localeCompare(b.date),
  key: 'date',
  render: (date: string, item: MydayType) => (
    <button type="button" className="link-button" onClick={() => onView(item)}>
      {formatDateOnly(new Date(date), i18nLanguage)}
    </button>
  ),
  responsive: ['xs', 'sm', 'lg'] as const,
});

export const createMeasurementsColumn = (t: TFunction, i18nLanguage: string, onView: (item: MydayType) => void) => ({
  title: t('myday.measurements'),
  dataIndex: 'measurements',
  sorter: (a: MydayType, b: MydayType) => (a.measurements?.length || 0) - (b.measurements?.length || 0),
  key: 'measurements',
  render: (measurements: MeasurementType[], item: MydayType) => {
    let mText = '';
    if (measurements?.length) {
      mText = `${measurements.length} ${t('common.items')}: `;
      measurements?.forEach((measurement: MeasurementType, index: number) => {
        mText += `${index !== 0 ? ', ' : ''}${getHealthMetricDescription(measurement.healthMetricType, i18nLanguage)}`;
      });
    } else {
      mText = '-';
    }
    return (
      <Tooltip title={mText}>
        <button type="button" className="link-button" style={{ maxWidth: '10rem' }} onClick={() => onView(item)}>
          {mText}
        </button>
      </Tooltip>
    );
  },
  responsive: ['sm'] as const,
});

export const createDescriptionColumn = (t: TFunction, onView: (item: MydayType) => void) => ({
  title: t('common.description'),
  dataIndex: 'description',
  sorter: (a: MydayType, b: MydayType) => a.description.localeCompare(b.description),
  key: 'description',
  render: (description: string, item: MydayType) => (
    <button type="button" className="link-button" onClick={() => onView(item)}>
      {description || '-'}
    </button>
  ),
  responsive: ['xs', 'sm', 'lg'] as const,
});

export const createFilesColumn = (t: TFunction, onView: (item: MydayType) => void) => ({
  title: t('myday.files'),
  dataIndex: 'file_count',
  sorter: (a: MydayType, b: MydayType) => a.file_count - b.file_count,
  key: 'file_count',
  render: (files: number, item: MydayType) => (
    <button type="button" className="link-button" onClick={() => onView(item)}>
      {files || '-'}
    </button>
  ),
  responsive: ['sm', 'lg'] as const,
});

export const createActionColumn = (t: TFunction, handlers: MydayActionHandlers) => ({
  title: t('myday.action'),
  key: 'action',
  render: (_: string, item: MydayType) => (
    <Space.Compact size="middle">
      <Tooltip title={t('myday.openMydayDetails')}>
        <Button
          shape="round"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handlers.onView(item)}
          className="edit-button"
        />
      </Tooltip>
      <Tooltip title={t('myday.editMydayTooltip')}>
        <Button
          shape="round"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handlers.onEdit(item)}
          className="edit-button"
        />
      </Tooltip>
      <Tooltip title={t('myday.deleteMydayTooltip')}>
        <Popconfirm
          title={t('myday.confirmDelete')}
          onConfirm={() => handlers.onDelete(item.id)}
          onCancel={() => console.log('Canceled')}
          okText={t('common.yes')}
          cancelText={t('common.no')}
        >
          <Button className="edit-button" shape="round" size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      </Tooltip>
    </Space.Compact>
  ),
  responsive: ['xs', 'sm', 'lg'] as const,
});

/**
 * Creates all standard columns for a Myday table
 */
export const createMydayColumns = (t: TFunction, i18nLanguage: string, handlers: MydayActionHandlers) => {
  return [
    createDateColumn(t, i18nLanguage, handlers.onView),
    createMeasurementsColumn(t, i18nLanguage, handlers.onView),
    createDescriptionColumn(t, handlers.onView),
    createFilesColumn(t, handlers.onView),
    createActionColumn(t, handlers),
  ];
};
